/**
 * /c/:chapterSlug/m/groups/:groupId — single group view.
 *
 * Hosts GroupMessages full-width. The group name is surfaced in the
 * MemberHeader breadcrumb (resolved from the sidebar's cached groups
 * list); GroupMessages no longer renders its own title section.
 *
 * Persists the last-selected groupId so a fresh visit to /groups
 * lands here without forcing the sidebar to pick the default.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { GroupMessages } from '@/components/member/GroupMessages';
import { foldersApi } from '@/services/api';
import type { Group } from '@/types';

const LAST_GROUP_IDS_KEY = 'navarasam96.messages.lastGroupIds';
const GROUPS_CACHE_KEY = 'navarasam96.sidebar.groupsCache';

function readCachedGroups(): Group[] {
  try {
    const raw = window.localStorage.getItem(GROUPS_CACHE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as { groups?: Group[] };
    return Array.isArray(parsed.groups) ? parsed.groups : [];
  } catch { return []; }
}

export function GroupView() {
  const params = useParams<{ chapterSlug: string; groupId: string }>();
  const navigate = useNavigate();
  const cachedAtMount = useMemo(() => readCachedGroups(), []);
  const [groups, setGroups] = useState<Group[]>(cachedAtMount);
  const [loading, setLoading] = useState<boolean>(cachedAtMount.length === 0);

  // Persist selection for next /groups visit.
  useEffect(() => {
    if (!params.groupId) return;
    try {
      window.localStorage.setItem(LAST_GROUP_IDS_KEY, JSON.stringify([params.groupId]));
    } catch { /* ignore */ }
  }, [params.groupId]);

  // Refresh in the background (sidebar usually beat us to it).
  useEffect(() => {
    let cancelled = false;
    foldersApi.list().then((folders) => {
      if (cancelled) return;
      setGroups(folders.flatMap((f) => f.groups ?? []));
    }).catch(() => undefined).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const group = useMemo(
    () => groups.find((g) => g.id === params.groupId) ?? null,
    [groups, params.groupId],
  );

  if (!group) {
    if (loading) return <div className="p-6 text-white/60">Loading group…</div>;
    return (
      <div className="p-6">
        <p className="text-red-400">Group not found or you don't have access.</p>
        <button
          type="button"
          onClick={() => navigate(`/c/${params.chapterSlug}/m/groups`)}
          className="link text-sm mt-2"
        >
          Pick another group →
        </button>
      </div>
    );
  }

  return <GroupMessages groupId={group.id} group={group} hideHeader />;
}

export default GroupView;
